/**
 * Bestand übernehmen.
 *
 * Der Weg in drei Schritten, und die Reihenfolge ist der Sinn:
 *
 *   1. Vorlage holen – leer, oder aus der Dokumentenablage gefüllt.
 *   2. In Excel durchsehen und ergänzen.
 *   3. Hochladen: zuerst als Probelauf, dann echt.
 *
 * Der Probelauf ist kein Zusatz. Bei dreihundert Zeilen ist die Frage
 * nicht, ob der Import läuft, sondern was er anlegt — und das gehört
 * gesehen, bevor es in der Datenbank steht. Der Knopf für die echte
 * Übernahme erscheint deshalb erst nach einem Probelauf.
 */

import { useRef, useState } from 'react'
import { type Importergebnis, useCsvEinlesen } from '../api/uebernahme'
import { Fehlerhinweis } from '../bausteine/Hinweise'

export function Uebernahme() {
  const einlesen = useCsvEinlesen()
  const [datei, setzeDatei] = useState<File | null>(null)
  const [probe, setzeProbe] = useState<Importergebnis | null>(null)
  const [fertig, setzeFertig] = useState<Importergebnis | null>(null)
  const [ordner, setzeOrdner] = useState('')
  const feld = useRef<HTMLInputElement>(null)

  function neueDatei(f: File | null) {
    setzeDatei(f)
    // Ein Probelauf gehört zu genau einer Datei. Bliebe er stehen, würde
    // der Knopf für die echte Übernahme Zahlen einer anderen Datei zeigen.
    setzeProbe(null)
    setzeFertig(null)
    einlesen.reset()
  }

  function laufen(probelauf: boolean) {
    if (!datei) return
    einlesen.mutate(
      { datei, probelauf },
      {
        onSuccess: (e) => {
          if (probelauf) {
            setzeProbe(e)
            setzeFertig(null)
          } else {
            setzeFertig(e)
            setzeProbe(null)
            setzeDatei(null)
            if (feld.current) feld.current.value = ''
          }
        },
      },
    )
  }

  return (
    <div className="space-y-4">
      <div className="karte space-y-3">
        <h2 className="text-xl font-bold">1. Vorlage holen</h2>
        <p className="text-base text-slate-700">
          Eine Zeile je Gerät. Der Kundenname steht in jeder Zeile — daran
          erkennt die Übernahme, was zusammengehört. Anschrift, Kontakt und
          Fälligkeiten nur in der ersten Zeile des Kunden; weitere Zeilen
          dürfen dort leer bleiben.
        </p>
        <div className="flex flex-wrap gap-2">
          <a className="knopf-zweit" href="/api/uebernahme/vorlage.csv">
            Leere Vorlage
          </a>
        </div>

        <div className="border-t-2 border-slate-200 pt-3">
          <p className="text-base text-slate-700">
            Oder aus der Dokumentenablage: die erste Ordnerebene wird als
            Kunde gelesen, die zweite als Spielplatz. Geräte stehen nicht in
            Ordnernamen und bleiben leer.
          </p>
          <label className="mt-2 block">
            <span className="text-base text-slate-700">
              Ordner in der Ablage (leer = ganze Bibliothek)
            </span>
            <input
              className="mt-1"
              value={ordner}
              placeholder="z. B. Kunden"
              onChange={(e) => setzeOrdner(e.target.value)}
            />
          </label>
          <a
            className="knopf-zweit mt-2 inline-block"
            href={`/api/uebernahme/ablage.csv?ordner=${encodeURIComponent(ordner)}`}
          >
            Vorlage aus der Ablage
          </a>
          <p className="mt-2 text-base text-slate-600">
            Gelesen wird nur. In der Ablage wird dabei nichts angelegt,
            verschoben oder gelöscht.
          </p>
        </div>
      </div>

      <div className="karte space-y-3">
        <h2 className="text-xl font-bold">2. Ausgefüllte Datei hochladen</h2>
        <input
          ref={feld}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => neueDatei(e.target.files?.[0] ?? null)}
        />

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="knopf-haupt"
            disabled={!datei || einlesen.isPending}
            onClick={() => laufen(true)}
          >
            {einlesen.isPending ? 'Liest …' : 'Probelauf'}
          </button>
          {probe && (
            <button
              type="button"
              className="knopf-warnung"
              disabled={einlesen.isPending}
              onClick={() => {
                const frage =
                  `Jetzt wirklich übernehmen?\n\n` +
                  `${probe.kunden_neu} Kunden, ${probe.plaetze_neu} Spielplätze, ` +
                  `${probe.geraete_neu} Geräte und ${probe.faelligkeiten_neu} ` +
                  `Fälligkeiten werden angelegt.`
                if (window.confirm(frage)) laufen(false)
              }}
            >
              Jetzt übernehmen
            </button>
          )}
        </div>

        <Fehlerhinweis fehler={einlesen.error} />
      </div>

      {probe && <Bericht ergebnis={probe} />}
      {fertig && <Bericht ergebnis={fertig} />}
    </div>
  )
}

function Bericht({ ergebnis }: { ergebnis: Importergebnis }) {
  const zahlen: [string, number, number | null][] = [
    ['Kunden', ergebnis.kunden_neu, ergebnis.kunden_bekannt],
    ['Spielplätze', ergebnis.plaetze_neu, ergebnis.plaetze_bekannt],
    ['Geräte', ergebnis.geraete_neu, ergebnis.geraete_bekannt],
    ['Ansprechpartner', ergebnis.kontakte_neu, null],
    ['Fälligkeiten', ergebnis.faelligkeiten_neu, null],
  ]

  return (
    <div className="karte space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-bold">
          {ergebnis.probelauf ? 'Probelauf' : 'Übernommen'}
        </h2>
        <span
          className={
            ergebnis.probelauf
              ? 'marke bg-stufe-1-grund text-stufe-1'
              : 'marke bg-stufe-ob-grund text-stufe-ob'
          }
        >
          {ergebnis.probelauf ? 'nichts gespeichert' : 'gespeichert'}
        </span>
        <span className="text-base text-slate-700">
          {ergebnis.zeilen} Zeilen gelesen
          {ergebnis.uebersprungen > 0 &&
            `, ${ergebnis.uebersprungen} übersprungen`}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-base">
          <thead>
            <tr className="text-left text-slate-600">
              <th className="py-1 pr-4">&nbsp;</th>
              <th className="py-1 pr-4 text-right tabular-nums">neu</th>
              <th className="py-1 text-right tabular-nums">schon vorhanden</th>
            </tr>
          </thead>
          <tbody>
            {zahlen.map(([text, neu, bekannt]) => (
              <tr key={text} className="border-t border-slate-200">
                <td className="py-1 pr-4 font-semibold">{text}</td>
                <td className="py-1 pr-4 text-right tabular-nums">{neu}</td>
                <td className="py-1 text-right tabular-nums text-slate-600">
                  {bekannt === null ? '—' : bekannt}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {ergebnis.unbekannte_spalten.length > 0 && (
        <p className="text-base text-slate-700">
          <span className="font-semibold">Unbekannte Spalten:</span>{' '}
          {ergebnis.unbekannte_spalten.join(', ')} — bleiben liegen.
        </p>
      )}

      {ergebnis.hinweise.length > 0 && (
        <div className="space-y-1">
          <h3 className="font-semibold">
            Hinweise ({ergebnis.hinweise.length}
            {ergebnis.weitere_hinweise > 0 && ` von ${ergebnis.hinweise.length + ergebnis.weitere_hinweise}`}
            )
          </h3>
          <ul className="max-h-80 space-y-1 overflow-y-auto text-base">
            {ergebnis.hinweise.map((h, i) => (
              <li
                key={i}
                className={
                  h.schwer
                    ? 'rounded-feld bg-stufe-3-grund px-2 py-1 text-stufe-3'
                    : 'px-2 py-1 text-slate-700'
                }
              >
                <span className="font-semibold tabular-nums">Zeile {h.zeile}</span>
                {h.feld && <> · {h.feld}</>} — {h.text}
              </li>
            ))}
          </ul>
          {ergebnis.weitere_hinweise > 0 && (
            <p className="text-base text-slate-600">
              {ergebnis.weitere_hinweise} weitere Hinweise werden nicht
              angezeigt.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
